import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiHelpCircle,
  FiImage,
  FiInfo,
  FiLayers,
  FiLock,
  FiMapPin,
  FiPackage,
  FiPlus,
  FiShield,
  FiTrash2,
  FiUploadCloud,
  FiVideo,
  FiX,
} from "react-icons/fi";
import { useLoadScript } from "@react-google-maps/api";
import { PenLineIcon } from "lucide-react";
import { RiMoneyDollarBoxLine } from "react-icons/ri";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import { useAuth } from "../../Components/Context/AuthContext";
import { s3Upload } from "../../Utils/s3Upload";
import AppLoader from "../../Components/Common/AppLoader";
import OTPModal from "../../Components/UserSideComponents/RequirementsPost/Components/OTPModal";
import AddressSelectModal from "../../Components/UserSideComponents/Home/Components/AddressSelectModal";
import AddAddressModalAPI from "../../Components/UserSideComponents/Home/Components/AddAddressModalAPI";
import ProductSpecModal from "../../Components/UserSideComponents/SupplierQuotations/ProductSpecModal";
import { assets } from "../../Assets/assets";

const GOOGLE_MAPS_LIBRARIES = ["places"];

/* ─── Helpers ───────────────────────────────────────────── */
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    : "—";

const IMAGE_SLOT_LABELS = [
  "Product Image 1",
  "Product Image 2",
  "Packaging Image",
];

const VIDEO_SLOT_LABELS = [
  "Product Video",
  "Packaging Video",
];

/* ─── Upload Dropzone Box ───────────────────────────────── */
const MediaUploadBox = ({
  type,
  onChange,
  disabled,
  count,
  max,
  breakdownText,
  supportedText,
}) => {
  const inputRef = useRef();

  return (
    <label
      className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-2xl p-4 sm:p-5 cursor-pointer transition-all duration-150 text-center ${disabled
        ? "opacity-50 cursor-not-allowed border-gray-300 bg-gray-50"
        : "border-primaryColor/40 hover:bg-primaryColor/5 hover:border-primaryColor bg-primaryColor/[0.02]"
        }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={type === "image" ? "image/*" : "video/*"}
        multiple
        hidden
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center ${type === "image" ? "bg-primaryColor/10 text-primaryColor" : "bg-emerald-50 text-emerald-600"
          }`}
      >
        {type === "image" ? <FiImage className="text-xl" /> : <FiVideo className="text-xl" />}
      </div>

      <div>
        <p className="text-sm font-bold text-gray-800">
          {type === "image" ? `Image (${count}/${max})` : `Video (${count}/${max})`}
        </p>
        <span
          className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${type === "image"
            ? "bg-pink-50 text-primaryColor border-pink-200"
            : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
        >
          {breakdownText}
        </span>
        <p className="text-[10px] text-gray-400 mt-1">{supportedText}</p>
      </div>

      <span
        className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full mt-1 ${count >= max ? "bg-emerald-100 text-emerald-700" : "bg-primaryColor/10 text-primaryColor"
          }`}
      >
        {count >= max ? `✓ All ${max} ${type}s uploaded` : `${count}/${max} uploaded`}
      </span>
    </label>
  );
};

/* ─── Image Previews Grid ───────────────────────────────── */
const MediaImagePreview = ({ files, onRemove }) => {
  return (
    <div className="grid grid-cols-3 gap-2.5 sm:gap-3 mt-3">
      {files.map((fileObj, i) => {
        const previewUrl = fileObj.preview || (typeof fileObj === "string" ? fileObj : URL.createObjectURL(fileObj));
        return (
          <div key={i} className="flex flex-col items-center">
            <div className="relative w-full h-20 sm:h-24 rounded-xl border-2 border-primaryColor/30 overflow-hidden group bg-gray-50 shadow-2xs">
              <img src={previewUrl} alt={`preview-${i}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 text-white text-xs rounded-full flex items-center justify-center shadow-md transition"
              >
                ✕
              </button>
            </div>
            <span className="text-[10px] font-bold text-gray-600 mt-1 truncate w-full text-center">
              {IMAGE_SLOT_LABELS[i] || `Image ${i + 1}`}
            </span>
          </div>
        );
      })}

      {/* Empty Slots */}
      {Array(Math.max(0, 3 - files.length))
        .fill(0)
        .map((_, i) => {
          const slotIndex = files.length + i;
          return (
            <div key={`empty-${i}`} className="flex flex-col items-center">
              <div className="w-full h-20 sm:h-24 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300">
                <FiImage className="text-xl" />
              </div>
              <span className="text-[10px] font-medium text-gray-400 mt-1 truncate w-full text-center">
                {IMAGE_SLOT_LABELS[slotIndex] || `Slot ${slotIndex + 1}`}
              </span>
            </div>
          );
        })}
    </div>
  );
};

/* ─── Video Previews Grid ───────────────────────────────── */
const MediaVideoPreview = ({ files, onRemove }) => {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mt-3">
      {files.map((fileObj, i) => {
        const previewUrl = fileObj.preview || (typeof fileObj === "string" ? fileObj : URL.createObjectURL(fileObj));
        return (
          <div key={i} className="flex flex-col items-center">
            <div className="relative w-full h-20 sm:h-24 rounded-xl border-2 border-emerald-500/30 overflow-hidden bg-black p-0.5 shadow-2xs">
              <video src={previewUrl} controls className="w-full h-full object-cover rounded-lg" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 text-white text-xs rounded-full flex items-center justify-center shadow-md transition"
              >
                ✕
              </button>
            </div>
            <span className="text-[10px] font-bold text-emerald-700 mt-1 truncate w-full text-center">
              {VIDEO_SLOT_LABELS[i] || `Video ${i + 1}`}
            </span>
          </div>
        );
      })}

      {/* Empty Slots */}
      {Array(Math.max(0, 2 - files.length))
        .fill(0)
        .map((_, i) => {
          const slotIndex = files.length + i;
          return (
            <div key={`empty-v-${i}`} className="flex flex-col items-center">
              <div className="w-full h-20 sm:h-24 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300">
                <FiVideo className="text-xl" />
              </div>
              <span className="text-[10px] font-medium text-gray-400 mt-1 truncate w-full text-center">
                {VIDEO_SLOT_LABELS[slotIndex] || `Slot ${slotIndex + 1}`}
              </span>
            </div>
          );
        })}
    </div>
  );
};

/* ─── Mobile Collapse Wrapper ──────────────────────────── */
const MobileCollapse = ({ title, icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-4 lg:hidden shadow-xs">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-gray-50/70 hover:bg-gray-100/70 transition"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs sm:text-sm font-bold text-gray-800">{title}</span>
        </div>
        <span className={`text-xs font-bold text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
          ▼
        </span>
      </button>
      {open && <div className="p-4 border-t border-gray-100">{children}</div>}
    </div>
  );
};

/* ─── Right Sidebar: Guidelines & Tips ─────────────────── */
const SupplierQuotationTips = () => {
  const tips = [
    {
      title: "1. Media First",
      desc: "Upload 3 clear images (2 product, 1 packaging) and 2 videos for maximum buyer confidence.",
    },
    {
      title: "2. Real Warehouse Location",
      desc: "Ensure your selected dispatch warehouse matches your actual stock location.",
    },
    {
      title: "3. Competitive Quoted Price",
      desc: "Quote your best competitive price per unit within the buyer target and cap limits.",
    },
    {
      title: "4. Accurate Specifications",
      desc: "Respond clearly to all buyer specification questions and attach test proofs if needed.",
    },
    {
      title: "5. Holding Guarantee",
      desc: "Reserve the quoted stock for the validity duration to ensure seamless fulfillment.",
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-xs space-y-4 lg:sticky lg:top-20">
      <div>
        <h3 className="text-xs sm:text-sm font-extrabold text-gray-800 flex items-center gap-2">
          📘 Quotation Guidelines
        </h3>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Follow best practices to maximize quotation acceptance
        </p>
      </div>

      <div className="space-y-3">
        {tips.map((tip, idx) => (
          <div key={idx} className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-primaryColor/10 text-primaryColor flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
              {idx + 1}
            </div>
            <div>
              <p className="text-xs font-bold text-gray-800">{tip.title}</p>
              <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">
                {tip.desc}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="p-3 bg-pink-50/60 border border-pink-200/60 rounded-xl text-[11px] text-primaryColor space-y-1">
        <p className="font-bold flex items-center gap-1.5">
          <FiShield className="text-xs flex-shrink-0" /> Verified Supplier Guarantee
        </p>
        <p className="text-[10px] text-gray-600 leading-relaxed">
          Quotations with matching physical media and test reports are prioritized by buyers.
        </p>
      </div>

      <div className="pt-2 border-t border-gray-100 text-[11px] text-gray-400 flex items-center gap-2">
        <FiHelpCircle className="text-primaryColor text-sm flex-shrink-0" />
        <span>
          Need help? Call Support:{" "}
          <a href="tel:+9108062177433" className="text-primaryColor font-semibold hover:underline">
            +9108062177433
          </a>
        </span>
      </div>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────── */
const SupplierCreateQuotation = () => {
  const { isLoaded: isMapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  // Supplier MOQ & Variety Info
  const [varietyCapPrice, setVarietyCapPrice] = useState(0);
  const [supplierMOQ, setSupplierMOQ] = useState(0);

  // Address State
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAddressData, setEditAddressData] = useState(null);

  // Media State (Arrays matching PostRequirement pattern)
  const [images, setImages] = useState([]); // Array of File objects (max 3)
  const [videos, setVideos] = useState([]); // Array of File objects (max 2)

  // Form States
  const [isAvailableFull, setIsAvailableFull] = useState(true);
  const [availableQty, setAvailableQty] = useState("");
  const [unitPrice, setUnitPrice] = useState("");

  // Pack Size
  const [weight, setWeight] = useState("");
  const [packWeightUnit, setPackWeightUnit] = useState("Kg");
  const [length, setLength] = useState("");
  const [breadth, setBreadth] = useState("");
  const [height, setHeight] = useState("");
  const [dimensionUnit, setDimensionUnit] = useState("cm");
  const [customDimensionUnit, setCustomDimensionUnit] = useState("");
  const [isCustomDimensionUnit, setIsCustomDimensionUnit] = useState(false);

  // Batch & Holding
  const [batchNumber, setBatchNumber] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    const future = new Date();
    future.setDate(future.getDate() + 7);
    return future.toISOString().split("T")[0];
  });

  // Specs & Requirements Modal State
  const [specs, setSpecs] = useState([]);
  const [otherReqs, setOtherReqs] = useState([]);
  const [specModalOpen, setSpecModalOpen] = useState(false);
  const [selectedSpecItem, setSelectedSpecItem] = useState(null);
  const [specType, setSpecType] = useState("spec"); // "spec" | "req"

  const openSpecModal = (item, type = "spec") => {
    setSelectedSpecItem(item);
    setSpecType(type);
    setSpecModalOpen(true);
  };

  const handleSpecSubmit = (radioValue, inputValue, proofFiles) => {
    if (!selectedSpecItem) return;

    if (specType === "spec") {
      setSpecs((prev) =>
        prev.map((s) =>
          s.id === selectedSpecItem.id || s.questionId === selectedSpecItem.questionId
            ? {
              ...s,
              checked: true,
              radioValue: radioValue === "Not Sure" ? "Not_Sure" : radioValue,
              inputValue: inputValue || "",
              proofFiles: proofFiles || [],
            }
            : s
        )
      );
    } else {
      setOtherReqs((prev) =>
        prev.map((r) =>
          r.id === selectedSpecItem.id
            ? {
              ...r,
              checked: true,
              radioValue: radioValue === "Not Sure" ? "Not_Sure" : radioValue,
              inputValue: inputValue || "",
              proofFiles: proofFiles || [],
            }
            : r
        )
      );
    }
  };

  const handleSpecClear = () => {
    if (!selectedSpecItem) return;

    if (specType === "spec") {
      setSpecs((prev) =>
        prev.map((s) =>
          s.id === selectedSpecItem.id || s.questionId === selectedSpecItem.questionId
            ? {
              ...s,
              checked: false,
              radioValue: null,
              inputValue: "",
              proofFiles: [],
            }
            : s
        )
      );
    } else {
      setOtherReqs((prev) =>
        prev.map((r) =>
          r.id === selectedSpecItem.id
            ? {
              ...r,
              checked: false,
              radioValue: null,
              inputValue: "",
              proofFiles: [],
            }
            : r
        )
      );
    }
  };

  // Declarations
  const [declarationSampleMatch, setDeclarationSampleMatch] = useState(false);
  const [declarationStockAvailable, setDeclarationStockAvailable] = useState(false);
  const [declarationPriceStability, setDeclarationPriceStability] = useState(false);

  // OTP Modal
  const [showOTPModal, setShowOTPModal] = useState(false);

  /* ─── Fetch Initial Order & Addresses ──────────────────── */
  const fetchData = useCallback(async () => {
    if (!orderId) {
      toast.error("Order ID is missing");
      navigate("/supplier-orders");
      return;
    }

    try {
      setLoading(true);

      // Fetch Order Details
      const orderRes = await axios.get(`/order/get-order-by-id/${orderId}`);
      if (!orderRes.data.success || !orderRes.data.data) {
        toast.error("Order not found");
        navigate("/supplier-orders");
        return;
      }
      const orderData = orderRes.data.data;
      setOrder(orderData);

      // Cap price & variety details
      const variety = orderData.productVariety;
      if (variety) {
        setVarietyCapPrice(Number(variety.cap_price || 0));
      }

      // Initialize specs from order
      if (orderData.specification?.relatedQuestionsAndAnswers?.length) {
        setSpecs(
          orderData.specification.relatedQuestionsAndAnswers.map((spec, i) => ({
            id: i + 1,
            questionId: spec.questionId,
            title: spec.question || `Specification #${i + 1}`,
            value: spec.answeredByBuyer || spec.answer || "N/A",
            checked: false,
            radioValue: null,
            inputValue: "",
            proofFiles: [],
          }))
        );
      }

      // Initialize other requirements
      if (orderData.specification?.otherRequirements?.length) {
        setOtherReqs(
          orderData.specification.otherRequirements.map((req, i) => ({
            id: i + 1,
            title: `Requirement #${i + 1}`,
            value: req.buyersRequirement || "N/A",
            checked: false,
            radioValue: null,
            inputValue: "",
            proofFiles: [],
          }))
        );
      }

      // Fetch Addresses
      const addrRes = await axios.get("/address");
      const addrList = addrRes?.data?.data || [];
      setAddresses(addrList);

      const defaultAddr =
        addrList.find((a) => a.addressType === "WareHouse" && a.isDefault) ||
        addrList.find((a) => a.addressType === "WareHouse") ||
        addrList.find((a) => a.isDefault) ||
        addrList[0] ||
        null;
      setSelectedAddress(defaultAddr);

      // Fetch Supplier MOQ
      const supplierId = user?._id || user?.id;
      if (supplierId && variety?._id) {
        try {
          const varRes = await axios.get(
            `/product-variety-supplier/get-varieties-by-supplier/${supplierId}?populate=true`
          );
          if (varRes.data.success && varRes.data.data) {
            const targetVarietyId = String(variety._id || variety);
            const matched = varRes.data.data.find(
              (v) =>
                String(v.variety?._id || v.varietyId?._id || v.varietyId || "") === targetVarietyId
            );
            if (matched) {
              setSupplierMOQ(matched.MOQ_For_Variety || 0);
            }
          }
        } catch (e) {
          console.warn("Could not fetch supplier variety MOQ:", e);
        }
      }
    } catch (err) {
      console.error("fetchData error:", err);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  }, [orderId, navigate, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh addresses
  const refreshAddresses = async () => {
    try {
      const addrRes = await axios.get("/address");
      const addrList = addrRes?.data?.data || [];
      setAddresses(addrList);
      const defaultAddr =
        addrList.find((a) => a.isDefault) || addrList[0] || null;
      if (!selectedAddress && defaultAddr) {
        setSelectedAddress(defaultAddr);
      }
    } catch (err) {
      console.error("refreshAddresses error:", err);
    }
  };

  /* ─── Calculations ──────────────────────────────────────── */
  const orderUnit =
    order?.priceAndQuantity?.standardizedUnit ||
    order?.productVariety?.standardizedUNIT ||
    "Kg";
  const buyerMOQ = Number(order?.priceAndQuantity?.MOQ || 0);
  const minPrice = Number(order?.priceAndQuantity?.expectedPriceRange?.min || 0);
  const maxPrice = Number(order?.priceAndQuantity?.expectedPriceRange?.max || 0);

  const effectiveQty = isAvailableFull ? buyerMOQ : Number(availableQty || 0);
  const numUnitPrice = Number(unitPrice || 0);
  const estimatedTotal = effectiveQty * numUnitPrice;

  // Validity Days
  const calculateValidityDays = () => {
    if (!fromDate || !toDate) return 0;
    try {
      const diff = new Date(toDate) - new Date(fromDate);
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    } catch {
      return 0;
    }
  };
  const validityDays = calculateValidityDays();

  /* ─── Validation ────────────────────────────────────────── */
  const validateForm = () => {
    if (!order) return "Order details not loaded.";
    if (!images || images.length < 3) {
      return "Please upload all 3 required images (2 Product Images + 1 Packaging Image).";
    }
    if (!videos || videos.length < 2) {
      return "Please upload all 2 required videos (1 Product Video + 1 Packaging Video).";
    }
    if (!selectedAddress) {
      return "Please select a dispatch warehouse location.";
    }
    if (!isAvailableFull) {
      if (!availableQty || Number(availableQty) <= 0) {
        return "Please enter your available quantity.";
      }
      if (supplierMOQ > 0 && Number(availableQty) < supplierMOQ) {
        return `Available quantity cannot be less than your MOQ (${supplierMOQ} ${orderUnit}).`;
      }
      if (buyerMOQ > 0 && Number(availableQty) > buyerMOQ) {
        return `Available quantity cannot exceed buyer required quantity (${buyerMOQ} ${orderUnit}).`;
      }
    }
    if (!unitPrice || numUnitPrice <= 0) {
      return "Please enter a valid quoted unit price.";
    }
    if (varietyCapPrice > 0 && numUnitPrice > varietyCapPrice) {
      return `Quoted price cannot exceed the Product Cap Price of ₹${varietyCapPrice}/${orderUnit}.`;
    }
    if (!weight || Number(weight) <= 0) {
      return "Please enter valid unit package weight.";
    }
    if (isCustomDimensionUnit && (!customDimensionUnit || !customDimensionUnit.trim())) {
      return "Please enter custom dimension unit.";
    }
    if (!length || !breadth || !height || Number(length) <= 0 || Number(breadth) <= 0 || Number(height) <= 0) {
      return "Please enter valid box dimensions (Length, Breadth, Height).";
    }
    if (!batchNumber.trim()) {
      return "Please enter the Batch / Lot Number.";
    }
    if (!fromDate || !toDate) {
      return "Please select valid holding period dates.";
    }
    if (new Date(toDate) < new Date(fromDate)) {
      return "Holding To Date cannot be earlier than From Date.";
    }
    if (!declarationSampleMatch || !declarationStockAvailable || !declarationPriceStability) {
      return "Please accept all 3 formal declaration checkboxes before submitting.";
    }

    return "";
  };

  const validationError = validateForm();
  const isFormValid = !validationError;

  /* ─── Media Handlers (PostRequirement Pattern) ──────────── */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (images.length + files.length > 3) {
      toast.error("Maximum 3 images allowed (2 Product Images, 1 Packaging Image)");
      return;
    }

    setImages((prev) => [...prev, ...files].slice(0, 3));
    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (videos.length + files.length > 2) {
      toast.error("Maximum 2 videos allowed (1 Product Video, 1 Packaging Video)");
      return;
    }

    setVideos((prev) => [...prev, ...files].slice(0, 2));
    e.target.value = "";
  };

  const handleRemoveVideo = (index) => {
    setVideos((prev) => prev.filter((_, idx) => idx !== index));
  };

  /* ─── Submit Trigger (Opens OTP) ────────────────────────── */
  const handleSubmitClick = () => {
    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg, { duration: 4000, icon: "⚠️" });
      return;
    }
    setShowOTPModal(true);
  };

  /* ─── OTP Verified & Final Submission ──────────────────── */
  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setUploadProgress("Uploading reference media to S3...");

    try {
      // 1. Upload Images to S3
      const uploadedImages = await Promise.all(
        images.map(async (fileObj) => {
          if (typeof fileObj === "string") return fileObj;
          const res = await s3Upload(fileObj, "quotations/supplier/images");
          return res.url;
        })
      );

      setUploadProgress("Uploading reference videos to S3...");

      // 2. Upload Videos to S3
      const uploadedVideos = await Promise.all(
        videos.map(async (fileObj) => {
          if (typeof fileObj === "string") return fileObj;
          const res = await s3Upload(fileObj, "quotations/supplier/videos");
          return res.url;
        })
      );

      setUploadProgress("Preparing quotation payload...");

      // 3. Process Specs & Proofs
      const processedSpecs = await Promise.all(
        specs
          .filter((s) => s.checked)
          .map(async (s) => {
            let proofUrls = [];
            if (s.proofFiles && s.proofFiles.length > 0) {
              proofUrls = await Promise.all(
                s.proofFiles.map(async (f) => {
                  if (typeof f === "string") return f;
                  const res = await s3Upload(f, "quotations/supplier/specs-proofs");
                  return res.url;
                })
              );
            }
            return {
              questionId: s.questionId,
              question: s.title,
              answeredByBuyer: s.value,
              supplierAction: {
                isAgreed:
                  s.radioValue === "Yes"
                    ? "Yes"
                    : s.radioValue === "No"
                      ? "No"
                      : "Not_Sure",
                thoughts: s.inputValue || "",
                proof: proofUrls,
              },
            };
          })
      );

      // 4. Process Other Requirements & Proofs
      const processedOtherReqs = await Promise.all(
        otherReqs
          .filter((r) => r.checked)
          .map(async (r) => {
            let proofUrls = [];
            if (r.proofFiles && r.proofFiles.length > 0) {
              proofUrls = await Promise.all(
                r.proofFiles.map(async (f) => {
                  if (typeof f === "string") return f;
                  const res = await s3Upload(f, "quotations/supplier/reqs-proofs");
                  return res.url;
                })
              );
            }
            return {
              buyersRequirement: r.value,
              supplierAction: {
                isAgreed:
                  r.radioValue === "Yes"
                    ? "Yes"
                    : r.radioValue === "No"
                      ? "No"
                      : "Not_Sure",
                thoughts: r.inputValue || "",
                proof: proofUrls,
              },
            };
          })
      );

      // 5. Construct Payload
      const quotationData = {
        orderId: order._id,
        media: {
          images: uploadedImages,
          videos: uploadedVideos,
        },
        referencePhotoAndVideo: {
          images: uploadedImages,
          videos: uploadedVideos,
        },
        batch_Lot_Number: batchNumber.trim(),
        specification: {
          relatedQuestionsAndAnswers: processedSpecs,
          otherRequirements: processedOtherReqs,
        },
        quantity: {
          isAvailableFull: isAvailableFull,
          volume: effectiveQty,
        },
        standardizedUnit: orderUnit,
        currency: "INR",
        pricing: {
          amount: numUnitPrice,
          label: `Price Per ${orderUnit}`,
        },
        packSize: {
          weight: parseFloat(weight) || 0,
          unit: packWeightUnit,
          dimensions: {
            measured: isCustomDimensionUnit ? customDimensionUnit.trim() : dimensionUnit,
            length: Number(length) || 0,
            breadth: Number(breadth) || 0,
            height: Number(height) || 0,
          },
        },
        holdingPeriod: {
          from: new Date(fromDate),
          to: new Date(toDate),
          validityDays: validityDays,
        },
        pickUpAddress: selectedAddress._id,
        declarations: [
          {
            label: "Sample Batch Matches Actual Stock",
            value: true,
            timestamp: new Date().toISOString(),
          },
          {
            label: "Sufficient Stock Available",
            value: true,
            timestamp: new Date().toISOString(),
          },
          {
            label: "Price Will Not Change Due to Market Fluctuation",
            value: true,
            timestamp: new Date().toISOString(),
          },
        ],
      };

      setUploadProgress("Submitting quotation offer...");

      // 6. Call Backend API
      const res = await axios.post("/quotation/create", quotationData);

      if (res.data.success) {
        toast.success("Quotation submitted successfully! 🎉", { duration: 4000 });
        navigate(`/supplier-quotations?orderId=${order._id}`);
      } else {
        toast.error(res.data.message || "Failed to create quotation");
      }
    } catch (err) {
      console.error("handleFinalSubmit error:", err);
      toast.error(err?.response?.data?.message || "Failed to submit quotation. Please try again.");
    } finally {
      setSubmitting(false);
      setUploadProgress("");
    }
  };

  if (loading) {
    return <AppLoader message="Loading Order Requirements..." />;
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl border border-gray-200 text-center max-w-md shadow-xs">
          <p className="text-gray-700 font-bold mb-3">Order not found or inaccessible.</p>
          <button
            onClick={() => navigate("/supplier-orders")}
            className="px-4 py-2 bg-primaryColor text-white font-semibold rounded-xl text-xs"
          >
            Back to Live Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="w-full">
      {/* ── HERO BANNER (Exact Matching RequirementFill.jsx) ─────────────────── */}
      <div
        className="w-full h-[140px] sm:h-[220px] md:h-[260px] lg:h-[300px] flex items-center justify-center bg-no-repeat bg-center bg-cover"
        style={{ backgroundImage: `url(${assets.requirementsbanner})` }}
      >
        <h1 className="text-subPrimaryColor text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold text-center px-4">
          Submit Your Best Quotation
        </h1>
      </div>

      {/* ── MAIN CONTAINER (3-COLUMN LAYOUT) ─────────────────────────────────── */}
      <div className="2xl:max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {/* Navigation Bar */}
        <div className="mb-5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(`/supplier-orders?orderId=${order._id}`)}
            className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-700 hover:text-primaryColor transition"
          >
            <FiArrowLeft className="text-base" /> Back to Order Details
          </button>
          <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-primaryColor/10 text-primaryColor border border-primaryColor/20">
            {order.stages?.replace(/_/g, " ") || "Live Order"} • Ref: {order.orderNumber || `#${order._id?.slice(-6)}`}
          </span>
        </div>

        {/* ── MOBILE COLLAPSIBLE SECTIONS ────────────────────────────────────── */}
        <MobileCollapse
          title="Order Summary & Live Preview"
          icon={<FiFileText className="text-primaryColor" />}
          defaultOpen={false}
        >
          {/* Mobile Order & Live summary content */}
          <div className="space-y-3 text-xs">
            <div className="bg-primaryColor/5 p-3 rounded-xl border border-primaryColor/15">
              <p className="text-[10px] font-bold text-gray-400 uppercase">Product Requirement</p>
              <p className="font-bold text-gray-900 mt-0.5">{order.product?.name}</p>
              <p className="text-gray-500 text-[11px]">{order.productVariety?.varietyName} • Grade: {order.productVariety?.grade || "A"}</p>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-primaryColor/10">
                <span className="text-gray-500">Buyer MOQ:</span>
                <span className="font-bold text-gray-800">{buyerMOQ.toLocaleString()} {orderUnit}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-500">Target Price:</span>
                <span className="font-bold text-[#772037]">₹{minPrice} - ₹{maxPrice}</span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-xl border border-gray-200">
              <p className="text-[10px] font-bold text-gray-400 uppercase">Your Quoted Offer</p>
              <div className="flex justify-between items-center mt-1.5">
                <span className="text-gray-500">Quoted Price:</span>
                <span className="font-bold text-[#772037]">₹{numUnitPrice.toLocaleString()} / {orderUnit}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-500">Quoted Qty:</span>
                <span className="font-bold text-gray-800">{effectiveQty.toLocaleString()} {orderUnit}</span>
              </div>
              <div className="flex justify-between items-center mt-1 pt-1.5 border-t border-gray-200">
                <span className="font-bold text-gray-700">Estimated Total:</span>
                <span className="font-extrabold text-base text-primaryColor">₹{Math.round(estimatedTotal).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </MobileCollapse>

        <MobileCollapse
          title="Quotation Guidelines"
          icon={<FiHelpCircle className="text-primaryColor" />}
          defaultOpen={false}
        >
          <SupplierQuotationTips />
        </MobileCollapse>

        {/* ── DESKTOP 3-COLUMN GRID ────────────────────────────────────────── */}
        <div className="lg:grid lg:grid-cols-12 gap-6 items-start">
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* 1. LEFT COLUMN: Order Details & Live Quotation Summary (Desktop)   */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="hidden lg:block lg:col-span-3 space-y-4 sticky top-20">
            {/* Target Order Card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-xs">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-primaryColor animate-pulse" />
                <h3 className="text-xs font-extrabold text-gray-900 uppercase tracking-wide">
                  Order Requirement
                </h3>
              </div>

              {/* Product Thumbnail & Name */}
              <div className="flex items-center gap-3 p-2 rounded-xl bg-gray-50 border border-gray-100 mb-3">
                <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {order.productVariety?.img || order.product?.img ? (
                    <img
                      src={order.productVariety?.img || order.product?.img}
                      alt="Product"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl">📦</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-gray-900 truncate">
                    {order.product?.name || "Product"}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {order.productVariety?.varietyName || "Standard Variety"}
                  </p>
                </div>
              </div>

              {/* Requirement Specs */}
              <div className="space-y-2 text-xs">
                {supplierMOQ > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Your MOQ:</span>
                    <span className="font-bold text-primaryColor">
                      {supplierMOQ.toLocaleString()} {orderUnit}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-gray-500">
                  <span>Buyer MOQ:</span>
                  <span className="font-bold text-gray-800">
                    {buyerMOQ.toLocaleString()} {orderUnit}
                  </span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Expected Price:</span>
                  <span className="font-bold text-[#772037]">
                    ₹{minPrice} - ₹{maxPrice}
                  </span>
                </div>
                {varietyCapPrice > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Variety Cap Price:</span>
                    <span className="font-bold text-gray-700">₹{varietyCapPrice}/{orderUnit}</span>
                  </div>
                )}
                {order.deliveryPeriod && (
                  <div className="flex justify-between text-gray-500 pt-2 border-t border-gray-100">
                    <span>Delivery Window:</span>
                    <span className="font-semibold text-gray-700 text-[11px]">
                      {fmtDate(order.deliveryPeriod.fromDate)} – {fmtDate(order.deliveryPeriod.toDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Live Calculated Quotation Offer Summary */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-primaryColor/20 rounded-2xl p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <h3 className="text-xs font-extrabold text-primaryColor uppercase tracking-wide flex items-center gap-1.5">
                  <RiMoneyDollarBoxLine className="text-sm" /> Live Offer Preview
                </h3>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Draft Active
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Quoted Price:</span>
                  <span className="font-extrabold text-sm text-[#772037]">
                    {numUnitPrice > 0 ? `₹ ${numUnitPrice.toLocaleString()}` : "—"}
                    <span className="text-[10px] text-gray-400 font-normal"> / {orderUnit}</span>
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Quoted Quantity:</span>
                  <span className="font-bold text-gray-800">
                    {effectiveQty > 0 ? `${effectiveQty.toLocaleString()} ${orderUnit}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Pack Weight:</span>
                  <span className="font-semibold text-gray-700">
                    {weight ? `${weight} ${packWeightUnit}` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Holding Validity:</span>
                  <span className="font-semibold text-gray-700">
                    {validityDays > 0 ? `${validityDays} Days` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Media Files:</span>
                  <span className="font-semibold text-gray-700">
                    {images.length}/3 Images • {videos.length}/2 Videos
                  </span>
                </div>
              </div>

              {/* Total Estimated Calculation Box */}
              <div className="mt-3 p-3 rounded-xl bg-primaryColor/10 border border-primaryColor/20 text-center">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">
                  Estimated Quotation Total
                </p>
                <p className="text-xl font-extrabold text-primaryColor mt-0.5">
                  ₹ {Math.round(estimatedTotal).toLocaleString("en-IN")}
                </p>
              </div>

              {/* Selected Warehouse badge */}
              {selectedAddress && (
                <div className="p-2.5 rounded-xl bg-white border border-gray-200 text-[11px] text-gray-600 flex items-start gap-2">
                  <FiMapPin className="text-primaryColor text-xs flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1 truncate">
                    <p className="font-bold text-gray-800 truncate">
                      {selectedAddress.addressType || "Warehouse"}: {selectedAddress.city}
                    </p>
                    <p className="text-[10px] text-gray-400 truncate">{selectedAddress.addressLine1 || selectedAddress.street}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* 2. CENTER COLUMN: Unified Create Quotation Form                    */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-6 space-y-6">
            {/* ─── SECTION 1: REFERENCE PHOTOS & VIDEOS (MATCHING POST REQUIREMENT + APP) ── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <div>
                  <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                    <FiImage className="text-primaryColor" /> 1. Reference Media Upload
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Upload verified product and packaging media from your warehouse.
                  </p>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-pink-50 text-[#772037] border border-pink-200 flex-shrink-0">
                  Mandatory
                </span>
              </div>

              {/* Upload Dropzones: Image on Left, Video on Right */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Images Upload Box */}
                <div>
                  <MediaUploadBox
                    type="image"
                    onChange={handleImageChange}
                    disabled={images.length >= 3}
                    count={images.length}
                    max={3}
                    breakdownText="Product image 2, Packaging image 1"
                    supportedText="Supported: .png, .jpeg, .jpg, .webp | Max: 5 MB"
                  />
                  <MediaImagePreview files={images} onRemove={handleRemoveImage} />
                </div>

                {/* Videos Upload Box */}
                <div>
                  <MediaUploadBox
                    type="video"
                    onChange={handleVideoChange}
                    disabled={videos.length >= 2}
                    count={videos.length}
                    max={2}
                    breakdownText="Product video 1, Packaging video 1"
                    supportedText="Supported: .mp4, .mov, .avi, .webm | Max: 25 MB"
                  />
                  <MediaVideoPreview files={videos} onRemove={handleRemoveVideo} />
                </div>
              </div>

              {/* Informational Guidance Note from Mobile Application */}
              <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200/80 rounded-xl px-3.5 py-2.5">
                <FiInfo className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  High-quality product and packaging media help buyers and inspectors accurately evaluate your lot quality before confirmation.
                </span>
              </div>
            </div>

            {/* ─── SECTION 2: WAREHOUSE / DISPATCH LOCATION (MATCHING POST REQUIREMENT) ── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                  <FiMapPin className="text-primaryColor" /> 2. Select Dispatch Location
                </h2>
                <span className="text-[10px] text-gray-400 font-medium">
                  Warehouse address
                </span>
              </div>

              <div className="pt-1">
                {selectedAddress ? (
                  <div
                    onClick={() => setShowAddressModal(true)}
                    className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-primaryColor text-white cursor-pointer hover:bg-primaryColor/90 transition shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/70">
                        Dispatching from ({selectedAddress.addressType || "Warehouse"})
                      </p>
                      <p className="text-sm font-semibold truncate mt-0.5">
                        {selectedAddress.addressLine1 || selectedAddress.street || selectedAddress.fullAddress}
                      </p>
                      <p className="text-xs text-white/70">
                        {[selectedAddress.city, selectedAddress.state, selectedAddress.pincode]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditAddressData(selectedAddress);
                        setShowAddModal(true);
                      }}
                      className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition"
                      title="Edit this warehouse address"
                    >
                      <PenLineIcon size={18} />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => setShowAddressModal(true)}
                    className="border-2 border-dashed border-primaryColor/40 p-4 rounded-xl text-center cursor-pointer hover:bg-primaryColor/5 transition"
                  >
                    <p className="text-sm font-semibold text-primaryColor">
                      + Add Warehouse / Dispatch Address
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ─── SECTION 3: QUANTITY & PRICING AVAILABILITY ───────────────── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                  <FiDollarSign className="text-primaryColor" /> 3. Quantity & Quoted Price
                </h2>
                {varietyCapPrice > 0 && (
                  <span className="text-[11px] font-semibold text-gray-500">
                    Cap Price: <strong className="text-gray-800">₹{varietyCapPrice}/{orderUnit}</strong>
                  </span>
                )}
              </div>

              {/* MOQ & Price Info Strip (Matching Application) */}
              <div className="grid grid-cols-3 gap-2 p-3 rounded-xl bg-gray-50/80 border border-gray-200 text-xs">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Your MOQ</p>
                  <p className="font-extrabold text-primaryColor mt-0.5 text-xs sm:text-sm">
                    {supplierMOQ > 0 ? `${supplierMOQ.toLocaleString()} ${orderUnit}` : `— ${orderUnit}`}
                  </p>
                </div>
                <div className="border-l border-gray-200 pl-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Order Quantity</p>
                  <p className="font-extrabold text-gray-900 mt-0.5 text-xs sm:text-sm">
                    {buyerMOQ.toLocaleString()} {orderUnit}
                  </p>
                </div>
                <div className="border-l border-gray-200 pl-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Target Range</p>
                  <p className="font-extrabold text-[#772037] mt-0.5 text-xs sm:text-sm">
                    ₹{minPrice} - ₹{maxPrice}
                  </p>
                </div>
              </div>

              {/* Full vs Partial Availability */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700">
                  Can you fulfill the full required quantity of{" "}
                  <strong className="text-primaryColor">{buyerMOQ.toLocaleString()} {orderUnit}</strong>?
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setIsAvailableFull(true)}
                    className={`py-2.5 px-4 rounded-xl text-xs font-bold border transition flex items-center justify-center gap-2 ${isAvailableFull
                      ? "bg-[#772037] text-white border-[#772037] shadow-sm shadow-[#772037]/20"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    <FiCheckCircle className="text-sm" /> Yes, Full MOQ ({buyerMOQ.toLocaleString()} {orderUnit})
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAvailableFull(false)}
                    className={`py-2.5 px-4 rounded-xl text-xs font-bold border transition flex items-center justify-center gap-2 ${!isAvailableFull
                      ? "bg-[#772037] text-white border-[#772037] shadow-sm shadow-[#772037]/20"
                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    No, Partial Volume
                  </button>
                </div>
              </div>

              {/* Partial Volume Input */}
              {!isAvailableFull && (
                <div className="space-y-1.5 p-3.5 rounded-xl bg-gray-50 border border-gray-200 animate-fadeIn">
                  <label className="text-xs font-bold text-gray-700">
                    Enter Available Quantity ({orderUnit}) *
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={availableQty}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setAvailableQty(val);
                      }
                    }}
                    placeholder={`e.g. ${supplierMOQ || Math.floor(buyerMOQ / 2)}`}
                    className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                  <p className="text-[11px] text-gray-500">
                    {supplierMOQ > 0 ? `Must be between your MOQ (${supplierMOQ} ${orderUnit}) and buyer request (${buyerMOQ} ${orderUnit})` : `Max allowed: ${buyerMOQ} ${orderUnit}`}
                  </p>
                </div>
              )}

              {/* Quoted Unit Price */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-700">
                    Quoted Unit Price (INR per {orderUnit}) *
                  </label>
                </div>

                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={unitPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setUnitPrice(val);
                      }
                    }}
                    placeholder="Enter unit price in ₹"
                    className="w-full pl-8 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>

                {varietyCapPrice > 0 && numUnitPrice > varietyCapPrice && (
                  <p className="text-[11px] font-bold text-red-500 mt-1">
                    ⚠️ Quoted price exceeds variety cap price of ₹{varietyCapPrice}/{orderUnit}.
                  </p>
                )}
              </div>
            </div>

            {/* ─── SECTION 4: PACKAGE DIMENSIONS & WEIGHT ──────────────────── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
              <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FiPackage className="text-primaryColor" /> 4. Package Dimensions & Unit Weight
              </h2>

              <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-700">Dimension Unit:</span>
                  <select
                    value={isCustomDimensionUnit ? "Other" : dimensionUnit}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "Other") {
                        setIsCustomDimensionUnit(true);
                      } else {
                        setIsCustomDimensionUnit(false);
                        setDimensionUnit(val);
                      }
                    }}
                    className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs font-semibold bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primaryColor"
                  >
                    <option value="cm">cm (centimeter)</option>
                    <option value="inch">inch</option>
                    <option value="mm">mm (millimeter)</option>
                    <option value="m">m (meter)</option>
                    <option value="ft">ft (feet)</option>
                    <option value="Other">Other (Custom Unit)</option>
                  </select>
                </div>

                {isCustomDimensionUnit && (
                  <div className="flex items-center gap-1.5 w-full sm:w-auto">
                    <input
                      type="text"
                      value={customDimensionUnit}
                      onChange={(e) => setCustomDimensionUnit(e.target.value)}
                      placeholder="e.g. yard, px"
                      className="px-3 py-1 border border-gray-300 rounded-lg text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-primaryColor"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-gray-700">Minimum Pack Weight *</label>
                    <button
                      type="button"
                      onClick={() => setPackWeightUnit((prev) => (prev === "Kg" ? "gram" : "Kg"))}
                      className="px-2 py-0.5 text-[10px] font-extrabold rounded-md bg-primaryColor/10 text-primaryColor border border-primaryColor/30 hover:bg-primaryColor/20 transition cursor-pointer"
                      title="Click to toggle between Kg and gram"
                    >
                      {packWeightUnit} ⇄
                    </button>
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={weight}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setWeight(val);
                      }
                    }}
                    placeholder={packWeightUnit === "Kg" ? "e.g. 25" : "e.g. 500"}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700">Length ({isCustomDimensionUnit ? (customDimensionUnit || "unit") : dimensionUnit}) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={length}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setLength(val);
                      }
                    }}
                    placeholder="e.g. 40"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700">Breadth ({isCustomDimensionUnit ? (customDimensionUnit || "unit") : dimensionUnit}) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={breadth}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setBreadth(val);
                      }
                    }}
                    placeholder="e.g. 30"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-700">Height ({isCustomDimensionUnit ? (customDimensionUnit || "unit") : dimensionUnit}) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={height}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d*$/.test(val)) {
                        setHeight(val);
                      }
                    }}
                    placeholder="e.g. 20"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>
              </div>
            </div>

            {/* ─── SECTION 5: BATCH NUMBER & ORDER HOLDING PERIOD ───────────── */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
              <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                <FiClock className="text-primaryColor" /> 5. Batch Number & Holding Period
              </h2>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">Batch / Lot Number *</label>
                <input
                  type="text"
                  value={batchNumber}
                  onChange={(e) => setBatchNumber(e.target.value)}
                  placeholder="e.g. PCM-28902GR"
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                />
              </div>

              {/* Symmetrical, perfectly aligned Holding Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-100">
                <div className="flex flex-col">
                  <div className="h-5 flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-gray-700">Holding From Date *</label>
                  </div>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full h-11 px-3.5 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>

                <div className="flex flex-col">
                  <div className="h-5 flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-gray-700">Holding To Date *</label>
                    {validityDays > 0 && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        {validityDays} Days Validity
                      </span>
                    )}
                  </div>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="w-full h-11 px-3.5 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                  />
                </div>
              </div>
            </div>

            {/* ─── SECTION 6: PRODUCT SPECIFICATIONS / BUYER Q&A ────────────── */}
            {specs.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                    <FiLayers className="text-primaryColor" /> 6. Product Specifications
                    <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-[11px] text-gray-500 font-medium">
                    Click items to verify
                  </span>
                </div>

                <div className="space-y-3">
                  {specs.map((s, idx) => {
                    const isChecked = s.checked;
                    const agreementTone =
                      s.radioValue === "Yes"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : s.radioValue === "No"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-amber-50 text-amber-700 border-amber-200";

                    return (
                      <div
                        key={s.id || idx}
                        onClick={() => openSpecModal(s, "spec")}
                        className={`p-3.5 sm:p-4 rounded-xl border transition-all duration-200 cursor-pointer flex items-center justify-between gap-3 ${isChecked
                          ? "bg-emerald-50/20 border-emerald-500/40 hover:border-emerald-500 shadow-2xs"
                          : "bg-gray-50/70 border-gray-200 hover:border-primaryColor/40 hover:bg-white"
                          }`}
                      >
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs font-bold text-gray-800">
                            Q: {s.title}
                          </p>
                          <p className="text-[11px] text-gray-600">
                            A: <span className="font-semibold text-gray-900">{s.value}</span>
                          </p>

                          {/* Status & Remarks if checked */}
                          {isChecked && (
                            <div className="pt-2 mt-1 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${agreementTone}`}>
                                {s.radioValue === "Not_Sure" ? "Not Sure" : s.radioValue}
                              </span>
                              {s.inputValue && (
                                <span className="text-[11px] text-gray-600 italic truncate max-w-xs">
                                  "{s.inputValue}"
                                </span>
                              )}
                              {s.proofFiles && s.proofFiles.length > 0 && (
                                <span className="text-[10px] font-bold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-full border border-primaryColor/20 flex items-center gap-1">
                                  <FiImage className="text-xs" /> Proof Attached
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Checkbox Button */}
                        <div
                          className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${isChecked
                            ? "bg-[#00D15B] text-white shadow-xs"
                            : "bg-white border border-gray-300 hover:border-gray-400"
                            }`}
                        >
                          {isChecked && <FiCheck className="text-sm stroke-[3]" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── SECTION 7: OTHER REQUIREMENTS ───────────────────────────── */}
            {otherReqs.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-xs space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <h2 className="text-sm sm:text-base font-extrabold text-gray-900 flex items-center gap-2">
                    <FiFileText className="text-primaryColor" /> 7. Other Buyer Requirements
                    <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-[11px] text-gray-500 font-medium">
                    Click items to verify
                  </span>
                </div>

                <div className="space-y-3">
                  {otherReqs.map((r, idx) => {
                    const isChecked = r.checked;
                    const agreementTone =
                      r.radioValue === "Yes"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : r.radioValue === "No"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-amber-50 text-amber-700 border-amber-200";

                    return (
                      <div
                        key={r.id || idx}
                        onClick={() => openSpecModal(r, "req")}
                        className={`p-3.5 sm:p-4 rounded-xl border transition-all duration-200 cursor-pointer flex items-center justify-between gap-3 ${isChecked
                          ? "bg-emerald-50/20 border-emerald-500/40 hover:border-emerald-500 shadow-2xs"
                          : "bg-gray-50/70 border-gray-200 hover:border-primaryColor/40 hover:bg-white"
                          }`}
                      >
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs font-bold text-gray-800">
                            {r.title || `Requirement #${idx + 1}`}
                          </p>
                          <p className="text-[11px] text-gray-600">
                            <span className="font-semibold text-gray-900">{r.value}</span>
                          </p>

                          {/* Status & Remarks if checked */}
                          {isChecked && (
                            <div className="pt-2 mt-1 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${agreementTone}`}>
                                {r.radioValue === "Not_Sure" ? "Not Sure" : r.radioValue}
                              </span>
                              {r.inputValue && (
                                <span className="text-[11px] text-gray-600 italic truncate max-w-xs">
                                  "{r.inputValue}"
                                </span>
                              )}
                              {r.proofFiles && r.proofFiles.length > 0 && (
                                <span className="text-[10px] font-bold text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded-full border border-primaryColor/20 flex items-center gap-1">
                                  <FiImage className="text-xs" /> Proof Attached
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Checkbox Button */}
                        <div
                          className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${isChecked
                            ? "bg-[#00D15B] text-white shadow-xs"
                            : "bg-white border border-gray-300 hover:border-gray-400"
                            }`}
                        >
                          {isChecked && <FiCheck className="text-sm stroke-[3]" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ─── SECTION 8: INLINE FORMAL DECLARATIONS (DIRECTLY ABOVE SUBMIT) ─── */}
            <div className="bg-white border-2 border-primaryColor/30 rounded-2xl p-4 sm:p-6 shadow-xs space-y-3">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                <FiShield className="text-primaryColor text-base" />
                <h3 className="text-xs sm:text-sm font-extrabold text-gray-900 uppercase tracking-wide">
                  Formal Supplier Declarations
                </h3>
              </div>
              <p className="text-xs text-gray-500">
                Please verify and check all 3 formal declarations before proceeding to submission:
              </p>

              <div className="space-y-3 pt-1">
                <label className="flex items-start gap-2.5 cursor-pointer p-2 rounded-xl hover:bg-gray-50 transition">
                  <input
                    type="checkbox"
                    checked={declarationSampleMatch}
                    onChange={(e) => setDeclarationSampleMatch(e.target.checked)}
                    className="w-4 h-4 text-primaryColor rounded border-gray-300 focus:ring-primaryColor mt-0.5"
                  />
                  <div className="text-xs text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">Sample Batch Matches Actual Stock:</strong> I confirm that the reference media and specifications match the actual physical batch ready at the warehouse.
                  </div>
                </label>

                <label className="flex items-start gap-2.5 cursor-pointer p-2 rounded-xl hover:bg-gray-50 transition">
                  <input
                    type="checkbox"
                    checked={declarationStockAvailable}
                    onChange={(e) => setDeclarationStockAvailable(e.target.checked)}
                    className="w-4 h-4 text-primaryColor rounded border-gray-300 focus:ring-primaryColor mt-0.5"
                  />
                  <div className="text-xs text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">Sufficient Stock Reserved:</strong> I confirm that sufficient stock is committed and reserved for the duration of the quoted holding period.
                  </div>
                </label>

                <label className="flex items-start gap-2.5 cursor-pointer p-2 rounded-xl hover:bg-gray-50 transition">
                  <input
                    type="checkbox"
                    checked={declarationPriceStability}
                    onChange={(e) => setDeclarationPriceStability(e.target.checked)}
                    className="w-4 h-4 text-primaryColor rounded border-gray-300 focus:ring-primaryColor mt-0.5"
                  />
                  <div className="text-xs text-gray-700 leading-relaxed">
                    <strong className="text-gray-900">Price Stability Guarantee:</strong> The quoted unit price is fixed and will not change due to market fluctuations upon buyer acceptance.
                  </div>
                </label>
              </div>
            </div>

            {/* ─── SECTION 9: SUBMISSION ACTION BUTTON ──────────────────────── */}
            <div className="space-y-2">
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmitClick}
                className={`w-full py-4 px-6 rounded-2xl text-sm sm:text-base font-extrabold text-white transition-all shadow-md flex items-center justify-center gap-2 ${isFormValid && !submitting
                  ? "bg-primaryColor hover:bg-primaryColor/90 shadow-primaryColor/25 hover:shadow-lg"
                  : "bg-gray-400 cursor-not-allowed opacity-90"
                  }`}
              >
                <FiLock className="text-lg" />
                {submitting ? uploadProgress || "Processing Quotation..." : "Verify & Submit Quotation Offer"}
              </button>

              {!isFormValid && (
                <p className="text-center text-xs text-amber-600 font-semibold flex items-center justify-center gap-1">
                  <FiInfo /> {validationError}
                </p>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* 3. RIGHT COLUMN: Guidelines & Tips Sidebar (Desktop)               */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="hidden lg:block lg:col-span-3">
            <SupplierQuotationTips />
          </div>
        </div>
      </div>

      {/* ─── MODALS ────────────────────────────────────────────────────────── */}
      {/* 0. Specification Verification Modal */}
      <ProductSpecModal
        isOpen={specModalOpen}
        onClose={() => setSpecModalOpen(false)}
        selectedSpec={selectedSpecItem}
        onSubmit={handleSpecSubmit}
        onClear={handleSpecClear}
      />

      {/* 1. Address Select Modal */}
      <AddressSelectModal
        showModal={showAddressModal}
        closeModal={() => setShowAddressModal(false)}
        addresses={addresses}
        setAddresses={setAddresses}
        setDefaultAddress={(addr) => {
          setSelectedAddress(addr);
          setShowAddressModal(false);
        }}
        refreshAddresses={refreshAddresses}
        openAddModal={() => {
          setEditAddressData(null);
          setShowAddressModal(false);
          setShowAddModal(true);
        }}
      />

      {/* 2. Add / Edit Address Modal */}
      {isMapsLoaded && (
        <AddAddressModalAPI
          showModal={showAddModal}
          closeModal={() => {
            setShowAddModal(false);
            setEditAddressData(null);
          }}
          refreshAddresses={refreshAddresses}
          type="WareHouse"
          editData={editAddressData}
          addresses={addresses}
        />
      )}

      {/* 3. OTP Verification Modal */}
      <OTPModal
        show={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        onVerified={handleFinalSubmit}
        identifier={user?.phone || user?.identifier}
      />

      {/* 4. Universal Blocking Loader */}
      {submitting && (
        <AppLoader
          blocking={true}
          message={uploadProgress || "Submitting Quotation Offer, please wait..."}
        />
      )}
    </section>
  );
};

export default SupplierCreateQuotation;
